if (!customElements.get('product-push-card')) {
  // Keep the actual Klaviyo mount nodes when filters or Depict replace the grid.
  // Recreating their HTML discards the form and its React/event-handler state.
  const embedNodes = new Map();
  let parking;

  customElements.define('product-push-card', class ProductPushCard extends HTMLElement {
    connectedCallback() {
      this.timers = [];
      this.embeds = [];
      this.onPageLoad = () => this.scheduleRetries();
      // Depict can connect a card before inserting its HTML, or insert the new
      // card before removing the old one. Handle both lifecycles.
      this.embedObserver = new MutationObserver(() => this.bindEmbeds());
      this.embedObserver.observe(this, { childList: true, subtree: true });
      this.bindEmbeds();
      if (document.readyState !== 'complete') {
        window.addEventListener('load', this.onPageLoad, { once: true });
      }
    }

    bindEmbeds() {
      if (!this.isConnected) return;
      const previous = this.embeds;
      this.embeds = Array.from(this.querySelectorAll('[class*="klaviyo-form-"]'))
        .map((node) => {
          const formClass = Array.from(node.classList).find((name) => /^klaviyo-form-[a-zA-Z0-9]+$/.test(name));
          if (!formClass || node.closest('form')) return null;

          const key = `${this.dataset.depictProductId}:${formClass}`;
          const saved = embedNodes.get(key);
          if (saved && saved !== node && saved.querySelector('form') && !node.querySelector('form')) {
            node.replaceWith(saved);
            node = saved;
          }
          embedNodes.set(key, node);
          return { key, node };
        }).filter(Boolean);

      if (document.readyState === 'complete' && this.embeds.some((embed) =>
        !previous.some((old) => old.key === embed.key && old.node === embed.node)
      )) {
        this.scheduleRetries();
      }
    }

    scheduleRetries() {
      this.timers.forEach(clearTimeout);
      // Klaviyo watches DOM insertions. Only retry empty mounts, never a live form.
      this.timers = [300, 1500, 4000, 10000, 20000].map((delay) => setTimeout(() => {
        if (!this.isConnected) return;
        this.embeds.forEach((embed) => {
          if (!this.contains(embed.node) || embed.node.childElementCount > 0) return;
          const fresh = document.createElement(embed.node.tagName);
          Array.from(embed.node.attributes).forEach(({ name, value }) => fresh.setAttribute(name, value));
          embed.node.replaceWith(fresh);
          embed.node = fresh;
          embedNodes.set(embed.key, fresh);
        });
      }, delay));
    }

    disconnectedCallback() {
      this.embedObserver?.disconnect();
      window.removeEventListener('load', this.onPageLoad);
      this.timers?.forEach(clearTimeout);
      // Keep mounted forms connected even while a filter temporarily removes
      // their product. Klaviyo must not see its React mount being destroyed.
      this.embeds?.forEach(({ node }) => {
        if (!this.contains(node) || !node.querySelector('form')) return;
        if (!parking) {
          parking = document.createElement('div');
          parking.hidden = true;
          parking.dataset.pushFormParking = '';
          document.body.appendChild(parking);
        }
        // Leave a mount placeholder if Depict later reconnects this same card.
        node.replaceWith(node.cloneNode(false));
        parking.appendChild(node);
      });
    }
  });
}

if (!customElements.get('facet-form')) {
  customElements.define(
    'facet-form',
    class FacetForm extends HTMLFormElement {
      constructor() {
        super();

        this.dirty = false;
        this.cachedMap = new Map();
        this.isMobile = theme.config.mqlSmall || theme.config.isTouch;
        this.motionReduced = theme.config.motionReduced || this.hasAttribute('motion-reduced');
        this.visibleProductCards = new Set();
        this.hasProductVisibilitySnapshot = false;
        this.renderRequestId = 0;
        this.renderTimer = null;

        this.addEventListener('change', this.onFormChange);
        this.addEventListener('submit', this.onFormSubmit);
      }

      connectedCallback() {
        this.resetProductVisibilityTracking();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        clearTimeout(this.renderTimer);
        this.visibilityObserver?.disconnect();
        this.productGridObserver?.disconnect();
        this.visibleProductCards.clear();
        this.visibilityObserver = null;
        this.productGridObserver = null;
        this.productGridContainer = null;
        this.hasProductVisibilitySnapshot = false;
      }

      getAnimationParams() {
        return {
          distance: this.isMobile ? 30 : 50,
          duration: this.motionReduced ? 0 : (this.isMobile ? 0.3 : 0.5),
          staggerDelay: this.motionReduced ? 0 : (this.isMobile ? 0.05 : 0.1)
        };
      }

      getVisibleItems(container) {
        if (this.hasProductVisibilitySnapshot) {
          return Array.from(this.visibleProductCards).filter((item) =>
            item.isConnected && this.productGridContainer?.contains(item)
          );
        }

        const visible = [];
        const gridItems = container.querySelector('motion-list')?.children || [];
        let measuredItems = 0;

        for (const item of gridItems) {
          if (!item.matches('.product-card')) continue;

          const rect = item.getBoundingClientRect();
          measuredItems++;
          if (rect.top >= window.innerHeight) {
            break;
          }

          if (rect.bottom > 0) visible.push(item);
          if (measuredItems >= 12) break;
        }

        return visible;
      }

      resetProductVisibilityTracking() {
        this.visibilityObserver?.disconnect();
        this.productGridObserver?.disconnect();
        this.visibleProductCards.clear();
        this.hasProductVisibilitySnapshot = false;
        this.productGridContainer = document.getElementById('ProductGridContainer');

        if (!this.productGridContainer || !('IntersectionObserver' in window)) return;

        this.visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              entry.target.isConnected &&
              this.productGridContainer?.contains(entry.target)
            ) {
              this.visibleProductCards.add(entry.target);
            } else {
              this.visibleProductCards.delete(entry.target);
            }
          });
          this.hasProductVisibilitySnapshot = true;
        }, { rootMargin: '200px 0px 200px 0px' });

        this.observeProductCards(this.productGridContainer);
        this.productGridObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.removedNodes.forEach((node) => this.unobserveProductCards(node));
            mutation.addedNodes.forEach((node) => this.observeProductCards(node));
          });
        });
        this.productGridObserver.observe(this.productGridContainer, {
          childList: true,
          subtree: true
        });
      }

      observeProductCards(root) {
        if (!(root instanceof Element) || !this.visibilityObserver) return;

        if (root.matches('.product-card')) this.visibilityObserver.observe(root);
        root.querySelectorAll('.product-card').forEach((item) =>
          this.visibilityObserver.observe(item)
        );
      }

      unobserveProductCards(root) {
        if (!(root instanceof Element) || !this.visibilityObserver) return;

        const items = root.matches('.product-card')
          ? [root, ...root.querySelectorAll('.product-card')]
          : root.querySelectorAll('.product-card');

        items.forEach((item) => {
          this.visibilityObserver.unobserve(item);
          this.visibleProductCards.delete(item);
        });
      }

      onFormChange() {
        this.dirty = true;
        this.dispatchEvent(new Event('submit', { cancelable: true }));
      }

      onFormSubmit(event) {
        event.preventDefault();
        if (!this.dirty) return;

        const url = this.buildUrl().toString();
        this.renderSection(url, event);
      }

      buildUrl() {
        const searchParams = new URLSearchParams(new FormData(this));
        const url = new URL(this.action);

        url.search = '';
        searchParams.forEach((value, key) => url.searchParams.append(key, value));

        ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
          if (url.searchParams.get(item) === '') {
            url.searchParams.delete(item);
          }
        });

        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      updateURLHash(url) {
        const clonedUrl = new URL(url);
        clonedUrl.searchParams.delete('section_id');
        history.replaceState({}, '', clonedUrl.toString());
      }

      beforeRenderSection({ preserveScroll = false } = {}) {
        const container = document.getElementById('ProductGridContainer');

        const { distance, duration, staggerDelay } = this.getAnimationParams();
        const visibleItems = this.getVisibleItems(container);

        Motion.timeline([
          [visibleItems, { y: distance, opacity: 0 }, { duration: duration, delay: Motion.stagger(staggerDelay) }],
          [container, { y: distance, opacity: 0 }, { duration: duration, easing: 'linear' }]
        ]);
        visibleItems.forEach(item => item.style.removeProperty('transform'));

        let headerHeight = 0;
        if (!theme.config.mqlSmall && document.querySelector('header.header')) {
          headerHeight = Math.round(document.querySelector('header.header').clientHeight);
        }
        setTimeout(() => {
          if (!preserveScroll) {
            const target = document.querySelector('.collection');
            window.scrollTo({
              top: target.getBoundingClientRect().top + window.scrollY - (theme.config.isTouch || theme.config.mqlSmall ? 95 : 20) - headerHeight,
              behavior: theme.config.motionReduced ? 'auto' : 'smooth'
            });
          }

          const drawer = document.getElementById('FacetDrawer');
          if (drawer) drawer.classList.add('loading');
        }, 100);
      }

      afterRenderSection() {
        const container = document.getElementById('ProductGridContainer');

        const { distance, duration, staggerDelay } = this.getAnimationParams();
        const visibleItems = this.getVisibleItems(container);

        Motion.timeline([
          [container, { y: [distance, 0], opacity: [0, 1] }, { duration: duration, easing: 'linear' }],
          [visibleItems, { y: [distance, 0], opacity: [0, 1] }, { duration: duration, delay: Motion.stagger(staggerDelay) }]
        ]);
        visibleItems.forEach(item => item.style.removeProperty('transform'));

        const drawer = document.getElementById('FacetDrawer');
        if (drawer) drawer.classList.remove('loading');

        document.dispatchEvent(new CustomEvent('collection:reloaded'));
      }

      renderSection(url, event, options = {}) {
        const requestId = ++this.renderRequestId;
        this.abortController?.abort();
        clearTimeout(this.renderTimer);

        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, event, requestId, options)
          : this.renderSectionFromFetch(url, event, requestId, options);

        if (this.hasAttribute('data-history')) this.updateURLHash(url);

        this.dirty = false;
      }

      renderSectionFromFetch(url, event, requestId, options) {
        this.abortController = new AbortController();
        
        this.beforeRenderSection(options);
        const start = performance.now();

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (requestId !== this.renderRequestId) return;
            const execution = (performance.now() - start);

            this.renderTimer = setTimeout(() => {
              if (requestId !== this.renderRequestId) return;
              const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
              this.renderFilters(parsedHTML, event);
              this.renderFiltersActive(parsedHTML);
              this.renderProductGridContainer(parsedHTML);
              this.renderProductCount(parsedHTML);
              this.renderSortBy(parsedHTML);

              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText, parsedHTML });
              this.cachedMap.set(url, responseText);

              this.afterRenderSection();
            }, execution > 500 ? 0 : 500);
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
      }

      renderSectionFromCache(url, event, requestId, options) {
        this.beforeRenderSection(options);

        this.renderTimer = setTimeout(() => {
          if (requestId !== this.renderRequestId) return;
          const responseText = this.cachedMap.get(url);
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.renderFilters(parsedHTML, event);
          this.renderFiltersActive(parsedHTML);
          this.renderProductGridContainer(parsedHTML);
          this.renderProductCount(parsedHTML);
          this.renderSortBy(parsedHTML);

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText, parsedHTML });

          this.afterRenderSection();
        }, 500);
      }

      renderFilters(parsedHTML, event) {
        const facetElements = parsedHTML.querySelectorAll(
          '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]'
        );

        const matchesIndex = (element) => {
          const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
          return jsFilter ? element.getAttribute('data-index') === jsFilter.getAttribute('data-index') : false;
        };
        const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

        facetsToRender.forEach((element) => {
          const filter = document.querySelector(`[data-filter][data-index="${element.getAttribute('data-index')}"]`);
          if (filter !== null) {
            if (filter.tagName === 'DETAILS') {
              filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
            }
            else {
              filter.innerHTML = element.innerHTML;
            }
          }
        });
      }

      renderFiltersActive(parsedHTML) {
        const id = 'FacetFiltersActive';
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
      }

      renderProductGridContainer(parsedHTML) {
        const id = 'ProductGridContainer';
        if (document.getElementById(id) === null) return;
        parsedHTML.querySelector('motion-list')?.setAttribute('motion-reduced', '');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
        this.resetProductVisibilityTracking();
      }

      renderProductCount(parsedHTML) {
        const id = 'ProductCount';
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
      }

      renderSortBy(parsedHTML) {
        const id = 'SortByContainer';
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('collection-filter-menu')) {
  customElements.define(
    'collection-filter-menu',
    class CollectionFilterMenu extends HTMLElement {
      constructor() {
        super();

        this.scrollAmount = 0;
        this.onScroll = this.updateArrows.bind(this);
        this.onResize = this.updateArrows.bind(this);
        this.onPrevClick = this.scrollPrevious.bind(this);
        this.onNextClick = this.scrollNext.bind(this);
        this.onLinkClick = this.applyFilter.bind(this);
        this.onCollectionReloaded = this.updateActiveLink.bind(this);
      }

      connectedCallback() {
        this.scroller = this.querySelector('[data-scroll-container]');
        this.prevButton = this.querySelector('[data-scroll-prev]');
        this.nextButton = this.querySelector('[data-scroll-next]');
        this.updateActiveLink();
        this.addEventListener('click', this.onLinkClick);
        document.addEventListener('collection:reloaded', this.onCollectionReloaded);

        if (!this.scroller || !this.prevButton || !this.nextButton) return;

        this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
        this.prevButton.addEventListener('click', this.onPrevClick);
        this.nextButton.addEventListener('click', this.onNextClick);
        window.addEventListener('resize', this.onResize);

        requestAnimationFrame(() => this.updateArrows());
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onLinkClick);
        document.removeEventListener('collection:reloaded', this.onCollectionReloaded);
        if (!this.scroller || !this.prevButton || !this.nextButton) return;

        this.scroller.removeEventListener('scroll', this.onScroll);
        this.prevButton.removeEventListener('click', this.onPrevClick);
        this.nextButton.removeEventListener('click', this.onNextClick);
        window.removeEventListener('resize', this.onResize);
      }

      applyFilter(event) {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const link = event.target.closest('.collection-filter-menu__link');
        if (!link || !this.contains(link) || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;

        const url = new URL(link.href, window.location.href);
        // Only reuse this collection's section for links to the same collection.
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.hash) return;

        const form = this.closest('.shopify-section')?.querySelector('form[is="facet-form"]');
        if (typeof form?.renderSection !== 'function') return;

        event.preventDefault();
        url.searchParams.delete('page');
        url.searchParams.set('section_id', theme.utils.sectionId(form));
        form.renderSection(url.toString(), event, { preserveScroll: true });
      }

      getStep() {
        return Math.max(180, Math.round(this.scroller.clientWidth * 0.7));
      }

      updateActiveLink() {
        const currentUrl = new URL(window.location.href);
        currentUrl.hash = '';

        this.querySelectorAll('.collection-filter-menu__link').forEach((link) => {
          const linkUrl = new URL(link.href, window.location.href);
          linkUrl.hash = '';
          const active = linkUrl.href === currentUrl.href;
          link.classList.toggle('is-active', active);
          link.classList.toggle('button--primary', active);
          link.classList.toggle('button--secondary', !active);
          if (active) {
            link.setAttribute('aria-current', 'page');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      }

      getMaxScroll() {
        return Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
      }

      updateArrows() {
        const maxScroll = this.getMaxScroll();
        const scrollLeft = Math.round(this.scroller.scrollLeft);

        this.prevButton.hidden = scrollLeft <= 1;
        this.nextButton.hidden = maxScroll <= 1 || scrollLeft >= maxScroll - 1;
      }

      scrollPrevious() {
        this.scrollBy(-this.getStep());
      }

      scrollNext() {
        this.scrollBy(this.getStep());
      }

      scrollBy(left) {
        this.scroller.scrollBy({
          left,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth'
        });
      }
    }
  );
}

if (!customElements.get('facet-count')) {
  customElements.define(
    'facet-count',
    class FacetCount extends HTMLElement {
      constructor() {
        super();
      }

      facetUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.facetUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, this.onFacetUpdate.bind(this));
      }

      disconnectedCallback() {
        if (this.facetUpdateUnsubscriber) {
          this.facetUpdateUnsubscriber();
        }
      }

      get itemCount() {
        return parseInt(this.innerText);
      }

      onFacetUpdate(event) {
        const parsedHTML = event.parsedHTML || new DOMParser().parseFromString(event.responseText, 'text/html');
        const facetCount = parsedHTML.querySelector('facet-count');
        this.innerText = facetCount.innerHTML;
        this.hidden = this.itemCount === 0;
      }
    }
  );
}

if (!customElements.get('results-count')) {
  customElements.define(
    'results-count',
    class ResultsCount extends HTMLElement {
      constructor() {
        super();
      }

      facetUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.facetUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, this.onFacetUpdate.bind(this));
      }

      disconnectedCallback() {
        if (this.facetUpdateUnsubscriber) {
          this.facetUpdateUnsubscriber();
        }
      }

      onFacetUpdate(event) {
        const parsedHTML = event.parsedHTML || new DOMParser().parseFromString(event.responseText, 'text/html');
        const resultsCount = parsedHTML.querySelector('results-count');
        this.innerText = resultsCount.innerHTML;
      }
    }
  );
}

if (!customElements.get('product-card-mobile-media')) {
  customElements.define(
    'product-card-mobile-media',
    class ProductCardMobileMedia extends HTMLElement {
      constructor() {
        super();

        this.onScroll = theme.utils.throttle(this.onScroll.bind(this));
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);
      }

      connectedCallback() {
        this.addEventListener('pointerdown', this.onPointerDown, theme.supportsPassive ? { passive: true } : false);
        this.addEventListener('pointermove', this.onPointerMove, theme.supportsPassive ? { passive: true } : false);
        this.addEventListener('pointerup', this.onPointerUp);
        this.addEventListener('pointercancel', this.onPointerUp);
        this.addEventListener('click', this.onClickCapture, true);
      }

      disconnectedCallback() {
        this.removeEventListener('pointerdown', this.onPointerDown, theme.supportsPassive ? { passive: true } : false);
        this.removeEventListener('pointermove', this.onPointerMove, theme.supportsPassive ? { passive: true } : false);
        this.removeEventListener('pointerup', this.onPointerUp);
        this.removeEventListener('pointercancel', this.onPointerUp);
        this.removeEventListener('click', this.onClickCapture, true);
        this.scroller?.removeEventListener('scroll', this.onScroll, theme.supportsPassive ? { passive: true } : false);
      }

      load() {
        if (this.hasAttribute('loaded')) return;

        const template = this.querySelector('template');
        if (!template) return;

        this.appendChild(template.content.cloneNode(true));
        template.remove();

        this.scroller = this.querySelector('.product-card__mobile-scroller');
        this.slides = this.scroller ? Array.from(this.scroller.children) : [];
        this.dots = Array.from(this.querySelectorAll('.product-card__mobile-dot'));

        if (!this.scroller || this.slides.length < 2) return;

        this.setAttribute('loaded', '');
        this.scroller.addEventListener('scroll', this.onScroll, theme.supportsPassive ? { passive: true } : false);
        this.updateActiveDot();
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse') return;

        this.startX = event.clientX;
        this.startY = event.clientY;
        this.dragged = false;
      }

      onPointerMove(event) {
        if (this.startX === undefined || event.pointerType === 'mouse') return;

        const deltaX = Math.abs(event.clientX - this.startX);
        const deltaY = Math.abs(event.clientY - this.startY);

        if (Math.max(deltaX, deltaY) > 8) {
          this.dragged = true;
        }

        if (!this.hasAttribute('loaded') && deltaX > 8 && deltaX > deltaY) {
          this.load();
          if (this.scroller && event.clientX < this.startX) {
            this.scroller.scrollLeft = deltaX;
          }
        }
      }

      onPointerUp() {
        if (this.dragged) {
          this.preventClickUntil = Date.now() + 350;
        }

        this.startX = undefined;
        this.startY = undefined;
      }

      onClickCapture(event) {
        if (this.preventClickUntil && Date.now() < this.preventClickUntil) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (!this.dragged) {
          const productUrl = this.getAttribute('data-product-url');

          if (productUrl) {
            window.location.href = productUrl;
          }
        }
      }

      onScroll() {
        this.updateActiveDot();
      }

      updateActiveDot() {
        const activeIndex = this.getActiveIndex();

        this.dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === activeIndex);
        });
      }

      getActiveIndex() {
        let activeIndex = 0;
        let closestOffset = Infinity;

        this.slides.forEach((slide, index) => {
          const offset = Math.abs(slide.offsetLeft - this.scroller.scrollLeft);

          if (offset < closestOffset) {
            closestOffset = offset;
            activeIndex = index;
          }
        });

        return activeIndex;
      }
    }
  );
}

if (!customElements.get('facet-remove')) {
  customElements.define(
    'facet-remove',
    class FacetRemove extends MagnetLink {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick(event) {
        const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

        if (form) {
          event.preventDefault();

          const url = new URL(this.href);
          url.searchParams.set('section_id', theme.utils.sectionId(form));
          form.renderSection(url.toString(), event);
        }
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('facet-sort')) {
  customElements.define(
    'facet-sort',
    class FacetSort extends HTMLElement {
      constructor() {
        super();

        this.onWindowClickHandler = this.onWindowClick.bind(this);

        Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });

        this.addEventListener('change', this.onChange);
        this.button.addEventListener('click', this.show.bind(this));
        this.close.addEventListener('click', this.hide.bind(this));
      }

      connectedCallback() {
        document.addEventListener('click', this.onWindowClickHandler);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onWindowClickHandler);
      }

      get listbox() {
        return this.querySelector('.sort-listbox');
      }

      get selection() {
        return this.querySelector('.sort-selection');
      }

      get button() {
        return this.querySelector('.sort-by');
      }

      get close() {
        return this.querySelector('.sort-close');
      }

      init() {
        this.initButton();
        this.style.setProperty('--facet-listbox-height', `${this.listbox.getBoundingClientRect().height.toFixed(1)}px`);
      }

      initButton() {
        const value = this.selection.innerText;
        const width = theme.getElementWidth(this.selection, value);
        this.style.setProperty('--facet-button-width', `${width}px`);
      }

      onChange(event) {
        const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

        if (form) {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', event.target.value);
          url.searchParams.set('section_id', theme.utils.sectionId(form));
          url.searchParams.delete('page');
          form.renderSection(url.toString(), event);
        }

        this.selection.innerText = event.target.nextElementSibling.innerText;
        this.initButton();
        this.hide();
      }

      show() {
        this.button.setAttribute('open', '');
      }

      hide(immediate = true) {
        if (this.button.hasAttribute('open')) {
          setTimeout(() => {
            this.button.removeAttribute('open');
          }, 100);

          if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;
          
          const btnFill = this.button.querySelector('[data-fill');
          Motion.animate(btnFill, { y: ['0%', immediate ? '0%' : '-76%'] }, { duration: 0.6, delay: immediate ? 0 : 0.2 });
        }
      }

      onWindowClick(event) {
        if (!this.contains(event.target)) {
          if (this.button.hasAttribute('open')) {
            this.hide(false);
          }
        }
      }
    }
  );
}

if (!customElements.get('facet-sticky')) {
  customElements.define(
    'facet-sticky',
    class FacetSticky extends HTMLElement {
      constructor() {
        super();

        new IntersectionObserver(this.handleIntersection.bind(this), { rootMargin: `-${screen.availHeight - 200}px 0px ${screen.availHeight}px 0px` }).observe(document.querySelector('.collection-section'));
      }

      get button() {
        return this.querySelector('.button');
      }

      handleIntersection(entries) {
        if (entries[0].isIntersecting) {
          Motion.animate(this.button, { opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
        }
        else {
          Motion.animate(this.button, { opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(15px)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
        }
      }
    }
  );
}

if (!customElements.get('price-range')) {
  customElements.define(
    'price-range',
    class PriceRange extends HTMLElement {
      constructor() {
        super();

        this.rangeMin = this.querySelector('input[type="range"]:first-child');
        this.rangeMax = this.querySelector('input[type="range"]:last-child');
        this.inputMin = this.querySelector('input[name="filter.v.price.gte"]');
        this.inputMax = this.querySelector('input[name="filter.v.price.lte"]');

        this.inputMin.addEventListener('focus', this.inputMin.select);
        this.inputMax.addEventListener('focus', this.inputMax.select);
        this.inputMin.addEventListener('change', this.onInputMinChange.bind(this));
        this.inputMax.addEventListener('change', this.onInputMaxChange.bind(this));

        this.rangeMin.addEventListener('change', this.onRangeMinChange.bind(this));
        this.rangeMax.addEventListener('change', this.onRangeMaxChange.bind(this));
        this.rangeMin.addEventListener('input', this.onRangeMinInput.bind(this));
        this.rangeMax.addEventListener('input', this.onRangeMaxInput.bind(this));
      }

      onInputMinChange(event) {
        event.preventDefault();
        event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1), event.target.min);
        this.rangeMin.value = event.target.value;
        this.rangeMin.parentElement.style.setProperty('--range-min', `${parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max) * 100}%`);
      }

      onInputMaxChange(event) {
        event.preventDefault();
        event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1), event.target.max);
        this.rangeMax.value = event.target.value;
        this.rangeMax.parentElement.style.setProperty('--range-max', `${parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max) * 100}%`);
      }

      onRangeMinChange(event) {
        event.stopPropagation();
        this.inputMin.value = event.target.value;
        this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onRangeMaxChange(event) {
        event.stopPropagation();
        this.inputMax.value = event.target.value;
        this.inputMax.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onRangeMinInput(event) {
        event.target.value = Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1);
        event.target.parentElement.style.setProperty('--range-min', `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        this.inputMin.value = event.target.value;
      }

      onRangeMaxInput(event) {
        event.target.value = Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1);
        event.target.parentElement.style.setProperty('--range-max', `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        this.inputMax.value = event.target.value;
      }
    }
  );
}

if (!customElements.get('infinite-button')) {
  customElements.define(
    'infinite-button',
    class InfiniteButton extends HoverButton {
      constructor() {
        super();

        this.onClickHandler = this.onClick.bind(this);
        this.onInViewHandler = this.onInView.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClickHandler);

        if (this.getAttribute('mode') == 'infinite') {
          this.stopInView = Motion.inView(this, this.onInViewHandler, { margin: '200px 0px 200px 0px' });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
        this.stopInView?.();
        this.abortController?.abort();
      }

      onInView() {
        this.onClick();
      }

      async onClick(event) {
        event?.preventDefault();
        if (this.hasAttribute('aria-busy')) return;

        const abortController = new AbortController();
        this.abortController = abortController;

        this.enableLoading();
        const url = this.buildUrl().toString();

        try {
          const responseText = await this.fetchSection(url, abortController.signal);
          if (abortController.signal.aborted) return;

          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.renderPagination(parsedHTML);
          this.renderProductGridContainer(parsedHTML);
        }
        catch (error) {
          if (!abortController.signal.aborted && error.name !== 'AbortError') {
            this.disableLoading();
            console.error(error);
          }
        }
        finally {
          if (this.abortController === abortController) this.abortController = null;
        }
      }

      async fetchSection(url, signal) {
        let response;

        try {
          response = await fetch(url, { signal });
        }
        catch (error) {
          if (signal.aborted || error.name === 'AbortError' || error.name !== 'TypeError') throw error;
          response = await fetch(url, { signal });
        }

        if (!response.ok) {
          throw new Error(`Unable to load collection page (${response.status})`);
        }

        return response.text();
      }

      renderPagination(parsedHTML) {
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer === null) return;

        const destination = productGridContainer.querySelector('.pagination');
        const source = parsedHTML.querySelector('.pagination');
        if (!destination) return;

        if (source) {
          const loadCount = parseInt(productGridContainer.dataset.infiniteLoadCount || '0') + 1;
          const nextButton = source.querySelector('button[is="infinite-button"]');

          productGridContainer.dataset.infiniteLoadCount = loadCount.toString();
          if (loadCount >= 2) {
            nextButton?.setAttribute('mode', 'click');
          }

          destination.innerHTML = source.innerHTML;
        }
        else {
          destination.remove();
        }
      }

      renderProductGridContainer(parsedHTML) {
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer === null) return;

        const destination = productGridContainer.querySelector('motion-list');
        const source = parsedHTML.querySelector('motion-list');

        if (source && destination) {
          const fragment = document.createDocumentFragment();
          const appendedItems = Array.from(source.querySelectorAll('.card'));

          appendedItems.forEach((item) => {
            item.querySelectorAll('img').forEach((image) => {
              image.loading = 'lazy';
              image.decoding = 'async';
              image.removeAttribute('fetchpriority');
            });
            fragment.appendChild(item);
          });
          destination.appendChild(fragment);

          destination.reload(appendedItems);
        }
      }
      
      buildUrl() {
        const action = new URL(this.getAttribute('action'), window.location.href);
        const url = new URL(`${action.pathname}${action.search}`, window.location.origin);
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      enableLoading() {
        this.classList.add('pointer-events-none');
        this.setAttribute('aria-busy', 'true');
      }

      disableLoading() {
        this.classList.remove('pointer-events-none');
        this.removeAttribute('aria-busy');
      }
    }, { extends: 'button' }
  );
}

if (!customElements.get('facets-topbar')) {
  customElements.define(
    'facets-topbar',
    class FacetsTopbar extends StickyElement {
      constructor() {
        super();
      }

      positionStickySidebar() {
        const bounding = this.getBoundingClientRect();
        if (bounding.top === 0) {
          document.body.classList.add('facets-topbar--active');
        }
        else {
          document.body.classList.remove('facets-topbar--active');
        }
      }
    }
  );
}

if (!customElements.get('model-view')) {
  customElements.define(
    'model-view',
    class ModelView extends MagnetButton {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });

        this._open = this.hasAttribute('open');
        this.onClickHandler = this.onClick.bind(this);
      }

      set open(value) {
        if (value !== this._open) {
          this._open = value;
          value ? this.setAttribute('open', '') : this.removeAttribute('open');
        }
      }

      static get observedAttributes() {
        return ['open'];
      }

      get open() {
        return this._open;
      }

      connectedCallback() {
        this.addEventListener('click', this.onClickHandler);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'open') {
          this.setActiveModel(newValue === '');
        }
      }

      init() {
        if (theme.config.hasLocalStorage) {
          const isActive = window.localStorage.getItem(`${theme.settings.themeName}:model-view`) || 'false';
          this.open = isActive === 'true';
        }
      }

      onClick(event) {
        event.preventDefault();
        this.open = !this.open;
      }

      setActiveModel(isActive) {
        const container = document.getElementById('ProductGridContainer');
        container.classList.toggle('lifestyle', isActive);

        if (theme.config.hasLocalStorage) {
          window.localStorage.setItem(`${theme.settings.themeName}:model-view`, isActive);
        }
      }
    }, { extends: 'button' }
  );
}
